import { OnInit, Component, ChangeDetectorRef, ViewChild } from '@angular/core';
import { filterPageTypes, toggleFilterTypes } from 'src/app/store/filters.era';
import { loadLocal, sleepForMS } from '../../helpers/helpers';
import { Performer, tableFilterTypes } from 'src/app/store/performers.era';
import { sessionsTableColumnsById } from 'src/app/helpers/table-helpers';
import { addAPerformer, saveTable } from '../performers/performerSharedFunctions';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { JoiEvent, Label, LabelObject } from 'globals/store/events.data';
import { sessionColumns, SessionTableData } from 'src/app/helpers/table-column-types';
import { TableColumn, TableMenuItem } from 'src/app/helpers/table.data';
import { ManageSessionData, ManageSessionComponent } from '../manage/manage-session.component';
import { ManagePerformerComponent, ManagePerformerData } from '../manage/manage-performer.component';
import { NestedTableComponent, TableIconEmitterData } from 'src/app/components/nested-table/nested-table.component';
import { Wrapper } from 'src/app/components/wrapper.component';
import { sessionTableReselector, sessionsColumnsReselector } from '../../store/reselectors/performers.reselector';
import { EntitySubTable, sessionSubTableTypes, subTableNames } from 'globals/store/entity.data';
import { LabelUpdateFunctionParams } from '../../components/label-marker/label-maker.dialog.component';
import { Session } from 'globals/store/programs.data';
import { addLabelsToMany, removeLabelsFromMany } from '../../helpers/label-helpers';
import { SessionInfo } from '../../store/reselectors/performerSponsors.reselector';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { JoiNavWrapper } from 'src/app/components/wrappers/joi-nav-wrapper.component';
import { TopMenuComponent } from './top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { traceNames } from 'src/app/services/performance.tracking.data';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { listDifference, listUnion } from 'globals/helpers/helpers';

@Component({
    selector: 'sessions-table',
    template: `
<top-menu></top-menu>
<mobile-menu></mobile-menu>
<joi-nav-wrapper 
  class="icon-tabs"
  [simple]="true"
  [menuItems]="subTables"
  [menuMap]="subTableNames"
  [selectedId]="subTable" 
  (selectItem)="subTable = $event"
></joi-nav-wrapper>
<command-menu
  [filterPage]="filterPage"
  [filterType]="filterType"
  [filterText]="'Filter'"
  [columnType]="sessionSubTableTypes[subTable]"
  [editColumnsTitle]="('Edit ' + subTableNames[subTable] + ' Columns') | titlecase"
  [showDottedMenu]="true"
  [showExport]="true"
  [showImport]="false"
  [canEditColumns]="true"
  (onExport)="saveTable()"
></command-menu>
<nested-table
  [filtersId]="filterPage"
  [items]="sessionData || []"
  [readOnly]="false"
  [condensedView]="condensed"
  [columns]="columnData[subTable]"
  [columnsById]="columnsById"
  [menuItems]="menuItems"
  [defaultSelectedColumnId]="defaultSessionColumn"
  (selectItem)="manageSession($event)"
  (updatedSelectedIds)="selectIds($event)"
  (iconClick)="toggleIcon($event)"
></nested-table>
<loader-pane-fragment [trace]="traceNames.viewSessions" ></loader-pane-fragment>
`,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [LoaderPaneFragmentComponent, TitleCasePipe, NestedTableComponent, CommandMenuComponent, JoiNavWrapper, TopMenuComponent, MobileMenuComponent],
})
export class SessionsTableComponent extends Wrapper<SessionsTableComponent> implements OnInit {
  filterPage = filterPageTypes.performerSessionsTable
  filterType = tableFilterTypes.SessionsTable
  defaultSessionColumn = sessionColumns.name
  traceNames = traceNames
  subTableNames = subTableNames
  columnData: { [t in EntitySubTable]: TableColumn[] }
  columnsById: { [id: string]: TableColumn } = sessionsTableColumnsById
  sessionTableRowsById: { [id:string]: SessionTableData } = {}

  condensed: boolean
  sessionData: SessionTableData[]
  allMenuItems: TableMenuItem[] = [ ]
  menuItems: TableMenuItem[] = this.allMenuItems // default to showing all menu items
  event: JoiEvent
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<SessionTableData>
  subTable: EntitySubTable = 'publish'
  subTables: EntitySubTable[] = ['publish', 'logistics', 'myView' ]
  sessionSubTableTypes = sessionSubTableTypes
  session_labels: LabelObject = {}
  selectedIds: string[] 

  saveTable() {
    saveTable('performer sessions' ,`${this.event.title} sessions table.csv`, this.dialog, this.nestedTable)
  }
  
  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Sessions", true)
    this.loadCondensed()
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subProp(this.store.select('events', 'labelMaps', 'session_labels'), 'session_labels')
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
    this.subCallback(this.store.select(sessionTableReselector), data => {
      if (!data) {
        this.sessionData = [] // for when the last is deleted
        return
      }
      const { sessionTableData, sessionTableRowsById } = data
      this.sessionData = sessionTableData
      this.sessionTableRowsById = sessionTableRowsById
    })
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false) 
    // console.log(this.condensed)
  }

  toggleIcon(d: TableIconEmitterData<SessionTableData>) {
    const row = d.row as SessionTableData
    const session = row.sessionInfo.fullSession
    switch (d.iconType) {
      case 'flag':
        session.flag = !session.flag
        this.programService.updateSession(row.sessionInfo.programId, session)      
        break
      default:
        console.log(`Unhandled icon type: ${d.iconType}`)
        break
    }
  }

  ngOnInit() {
    this.subProp(this.store.select(sessionsColumnsReselector), 'columnData')
  }

  async manageSession({ sessionInfo }: SessionTableData) {
    const data: ManageSessionData = { sessionId: sessionInfo.id }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManageSessionComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  async openPerformerManager(performer: Performer, autoFocus = false) {
    const data: ManagePerformerData = { performerId: performer.id, readOnly: !this.event || this.event.is_readonly }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManagePerformerComponent, { disableClose: true, autoFocus, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  addAPerformer(event) {
    addAPerformer(event, this.dialog, this.performerService, this.openPerformerManager.bind(this))
  }

  selectIds(ids: Set<string>) {
    this.selectedIds = Array.from(ids)
    // console.log(this.selectedTaskIds)
    setTimeout(() => this.buildMenu(), 0)
  }

  private get selectedSessionInfos(): SessionInfo[] {
    return (this.selectedIds || []).map(id => this.sessionTableRowsById[id].sessionInfo)
  }

  private get selectedSessions(): Session[] {
    return this.selectedSessionInfos.map(i => i.fullSession)
  }

  buildMenu() {
    const sessions = this.selectedSessionInfos
    const n = sessions.length
    this.menuItems = []
    this.menuItems.push({ title: 'Add labels', method: this.addLabels.bind(this) })
    if (sessions.some(s => s.fullSession.labels?.length)) this.menuItems.push({ title: 'Remove labels', method: this.removeLabels.bind(this) })
  }


  public async updateLabelTemplate(labels: Label[]) {
    labels.forEach(label => this.session_labels[label.id] = label) // need to overwrite the old values here to both maintain order and propagate changes without losing filtered out template labels
    await this.eventService.updateEvent(this.eventService.eventId, { session_labels: Object.values(this.session_labels) })
  }

  public async addLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)
    if (!labelsToggled) return

    const labelUpdates: { [programId:string]: { sessionId: string, labels: string[] }[] } = {}
    this.selectedSessionInfos
      .filter(s => !s.fullSession.labels || labels.some(label => !s.fullSession.labels.includes(label)))
      .forEach(({ programId, fullSession: { labels: sessionLabels, id: sessionId } }) => {
        if (!labelUpdates[programId]) labelUpdates[programId] = []
        labelUpdates[programId].push({ sessionId, labels: listUnion(sessionLabels, labels)  })
      })
    await this.programService.updateManySessionLabels(labelUpdates)
    await sleepForMS(1000)   
    this.buildMenu()
  }

  public async removeLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const filteredLabels = labelTemplate.map(l => l.id).filter(id => !labels.includes(id))
    if (filteredLabels.length === 0) return

    const labelUpdates: { [programId:string]: { sessionId: string, labels: string[] }[] } = {}
    this.selectedSessionInfos
      .filter(s => s.fullSession.labels && s.fullSession.labels.some(label => filteredLabels.includes(label)))
      .forEach(({ programId, fullSession: { labels: sessionLabels, id: sessionId } }) => {
        if (!labelUpdates[programId]) labelUpdates[programId] = []
        labelUpdates[programId].push({ sessionId, labels: listDifference(sessionLabels, filteredLabels) })
      })

    await this.programService.updateManySessionLabels(labelUpdates)  
    await sleepForMS(1000)
    this.buildMenu()    
  }

  async removeLabels(_, event) { 
    removeLabelsFromMany(this.removeLabelsUpdateFunction.bind(this), this.session_labels, this.dialog, 'session', this.selectedSessions, event)
  }

  async addLabels(_, event) { 
    addLabelsToMany(this.addLabelsUpdateFunction.bind(this), this.session_labels, this.dialog, 'session', this.selectedSessions, event)
  }


}
